import * as ToastPrimitives from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { Cancel } from "@/assets/icons";
import { ToastStateEnum } from "@/enums";
import { generateValidClassNameHandler } from "@/lib/utils";

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
    React.ElementRef<typeof ToastPrimitives.Viewport>,
    React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({
    className,
    ...props
}, ref) => (
    <ToastPrimitives.Viewport
        ref={ref}
        className={generateValidClassNameHandler(
            "fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-2 md:p-4 sm:bottom-0 sm:left-0 sm:top-auto sm:flex-col md:max-w-[420px]",
            className,
        )}
        {...props}
    />
));

ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
    "group pointer-events-auto relative flex w-full items-center justify-between space-x-2 overflow-hidden rounded border p-6 pr-8 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-left-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full md:space-x-4",
    {
        defaultVariants: { variant: ToastStateEnum.DEFAULT },
        variants: {
            variant: {
                danger: "border-danger bg-danger-bg text-gray-600",
                default: "border-foreground bg-background text-gray-600",
                success: "border-success bg-success-bg text-gray-600",
            },
        },
    },
);

const Toast = React.forwardRef<
    React.ElementRef<typeof ToastPrimitives.Root>,
    React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants>
>(({
    className,
    variant,
    ...props
}, ref) => (
    <ToastPrimitives.Root
        ref={ref}
        className={generateValidClassNameHandler(
            toastVariants({ variant }),
            variant && `group ${variant}`,
            className,
        )}
        onPointerDown={(e) => e.stopPropagation()}
        {...props}
    />
));

Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
    React.ElementRef<typeof ToastPrimitives.Action>,
    React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({
    className,
    ...props
}, ref) => (
    <ToastPrimitives.Action
        ref={ref}
        className={generateValidClassNameHandler(
            "inline-flex h-8 shrink-0 items-center justify-center rounded border bg-transparent px-3 text-xs md:text-sm font-medium transition-colors hover:bg-secondary disabled:pointer-events-none disabled:opacity-50 group-[.danger]:border-gray-300 group-[.danger]:hover:border-danger group-[.danger]:hover:bg-danger group-[.danger]:hover:text-danger-bg",
            className,
        )}
        {...props}
    />
));

ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({
    className,
    ...props
}, ref) => (
    <ToastPrimitives.Close
        ref={ref}
        toast-close=""
        className={generateValidClassNameHandler(
            "absolute right-2 top-2 rounded p-1 opacity-0 transition-opacity focus:opacity-100 focus:outline-none group-hover:opacity-100",
            "group-[.default]:text-foreground",
            "group-[.danger]:text-danger",
            "group-[.success]:text-success",
            className,
        )}
        onPointerDown={(e) => e.stopPropagation()}
        {...props}
    >
        <Cancel size={16} />
    </ToastPrimitives.Close>
));

ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
    React.ElementRef<typeof ToastPrimitives.Title>,
    React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({
    className,
    ...props
}, ref) => (
    <ToastPrimitives.Title
        ref={ref}
        className={generateValidClassNameHandler(
            "text-xs md:text-sm font-semibold",
            "group-[.default]:text-gray-foreground",
            "group-[.danger]:text-danger",
            "group-[.success]:text-success",
            className,
        )}
        {...props}
    />
));

ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
    React.ElementRef<typeof ToastPrimitives.Description>,
    React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({
    className,
    ...props
}, ref) => (
    <ToastPrimitives.Description
        ref={ref}
        className={generateValidClassNameHandler(
            "text-xs md:text-sm opacity-90",
            className,
        )}
        {...props}
    />
));

ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>

type ToastActionElement = React.ReactElement<typeof ToastAction>

export {
    Toast,
    ToastAction,
    type ToastActionElement,
    ToastClose,
    ToastDescription,
    type ToastProps,
    ToastProvider,
    ToastTitle,
    ToastViewport,
};
